// Synchronisation over the blind relay (ARCHITECTURE.md §8).
//
// Publish: seal my profile once, wrap it per friend, sign each envelope, push.
// Receive: fetch my inbox (or get a live push), verify+open each envelope, and
// cache the profile of any sender who is actually in my friends list. Mutual
// connection falls out naturally — I only surface updates from people I added,
// and they only receive mine if I added them.

import { relay } from './config';
import { emptyProfile, getMyRecord, isEmptyProfile, putMyRecord, touchVersion, type ProfileRecord } from './profile';
import {
  addFriend,
  getFriend,
  listFriendEntries,
  listFriends,
  purgeFriend,
  reconcileFriends,
  removeFriend,
  type Friend,
} from './friends';
import { listGroupEntries, purgeGroups, reconcileGroups, type Group } from './groups';
import {
  buildEnvelope,
  buildEnvelopeForRecipient,
  openBackupEnvelope,
  openEnvelope,
  sealBackupRecord,
  sealProfileRecord,
  type BackupRecord,
  type Envelope,
} from './envelope';
import { cacheProfileIfNewer, removeCachedProfile } from './cache';
import { clearPublishPending, markPublishPending } from './publishQueue';
import { idbGet, idbPut, STORE_IDENTITY } from './db';
import type { StoredIdentity } from './identity';

const BACKUP_VERSION_KEY = 'backupVersion';

/**
 * The next version to stamp on a self-backup. It needs its own counter rather
 * than the profile's edit version, because a contacts/groups-only change never
 * bumps the profile — and the relay keeps only the highest-versioned envelope
 * per author, silently dropping anything that doesn't strictly advance.
 *
 * Wall-clock time is the base so versions stay meaningful across devices, but
 * a persisted floor guarantees the strict increase the relay demands even when
 * the clock doesn't cooperate: two edits inside the same millisecond, or a
 * clock that jumps backwards (NTP correction, manual change, a device with a
 * flat RTC battery). Without the floor those backups vanish silently, which is
 * the worst possible failure for a backup.
 */
async function nextBackupVersion(): Promise<number> {
  const last = (await idbGet<number>(STORE_IDENTITY, BACKUP_VERSION_KEY)) ?? 0;
  const version = Math.max(Date.now(), last + 1);
  await idbPut(STORE_IDENTITY, BACKUP_VERSION_KEY, version);
  return version;
}

/** Raise the backup floor so later backups out-rank one we just restored. */
async function raiseBackupFloor(version: number): Promise<void> {
  const last = (await idbGet<number>(STORE_IDENTITY, BACKUP_VERSION_KEY)) ?? 0;
  if (version > last) await idbPut(STORE_IDENTITY, BACKUP_VERSION_KEY, version);
}

/** Build the self-only envelope backing up my profile, contacts and groups. */
async function buildSelfBackupEnvelope(
  id: StoredIdentity,
  profile: ProfileRecord,
  friends: Friend[],
  groups: Group[],
): Promise<Envelope> {
  const backup: BackupRecord = {
    version: await nextBackupVersion(),
    updatedAt: Date.now(),
    profile,
    friends,
    groups,
  };
  const { contentKey, content } = await sealBackupRecord(backup);
  return buildEnvelope(id.keys, id.circleId, id.circleId, backup.version, content, contentKey);
}

/**
 * How long a tombstone is kept before being dropped for good. It only has to
 * outlive the longest a second device might stay offline; past that, keeping it
 * would grow the backup forever.
 */
const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

/** Forget tombstones old enough that no device could still be unaware of them. */
async function purgeOldTombstones(): Promise<void> {
  const cutoff = Date.now() - TOMBSTONE_TTL_MS;
  await purgeGroups(cutoff);
  for (const f of await listFriendEntries()) {
    if (f.deletedAt !== undefined && f.deletedAt <= cutoff) await purgeFriend(f.circleId);
  }
}

/**
 * Push a fresh self-backup (profile + contacts + groups) without touching
 * friends. Used after a contacts/groups-only change (adding to a group,
 * verifying a contact, …) that `publishMyProfile` wouldn't otherwise cover.
 * Best-effort: a failure here just leaves the previous backup in place until
 * the next successful publish.
 */
export async function publishSelfBackup(id: StoredIdentity): Promise<void> {
  if (!relay.configured) return;
  await purgeOldTombstones();
  const profile = await getMyRecord();
  // Tombstones included: a removal only reaches the other devices if it travels.
  const friends = await listFriendEntries();
  const groups = await listGroupEntries();
  if (profile.version === 0 && friends.length === 0 && groups.length === 0) return;
  try {
    await relay.publish(id, [await buildSelfBackupEnvelope(id, profile, friends, groups)]);
  } catch {
    /* best-effort — next publish (or heartbeat retry) will carry it */
  }
}

/**
 * Why a publish did nothing, so the UI can tell the user instead of failing
 * silently — the single most confusing part of "I added a friend but they see
 * nothing".
 */
export type PublishSkip = 'relay-off' | 'empty-profile' | 'no-friends';

export interface PublishResult {
  published: number;
  skipped?: PublishSkip;
}

/** Publish my current profile to every friend. No-op if the relay is off. */
export async function publishMyProfile(id: StoredIdentity): Promise<PublishResult> {
  if (!relay.configured) return { published: 0, skipped: 'relay-off' };

  await purgeOldTombstones();
  const record = await getMyRecord();
  const friends = await listFriends();
  // The backup carries tombstones too, so removals reach my other devices.
  const friendEntries = await listFriendEntries();
  const groupEntries = await listGroupEntries();
  const hasProfile = record.version > 0;

  const envelopes: Envelope[] = [];

  // An encrypted self-backup addressed to myself, covering whatever there is
  // to protect — profile, contacts, groups — so a recovery on a new device
  // restores the app's whole local state, not just the keys. Published even
  // with no friends yet, and even before the profile itself has ever been
  // filled in (e.g. contacts added first).
  if (hasProfile || friendEntries.length > 0 || groupEntries.length > 0) {
    envelopes.push(await buildSelfBackupEnvelope(id, record, friendEntries, groupEntries));
  }

  // Version 0 means the profile was never saved: there is nothing to share
  // with friends, and this is the trap most new users fall into — reported
  // explicitly below, once we know whether the self-backup went out.
  let friendEnvelopes: Envelope[] = [];
  if (hasProfile) {
    const { contentKey, content } = await sealProfileRecord(record);
    // allSettled, not all: one friend with corrupt local data (bad Circle ID,
    // broken key) must not throw here — that would abort the self-backup and
    // every other friend's envelope too, before the try/catch below ever runs,
    // leaving `pendingPublish` permanently stuck since the same build failure
    // would then reproduce identically on every retry.
    const results = await Promise.allSettled(
      friends.map((f) => buildEnvelope(id.keys, id.circleId, f.circleId, record.version, content, contentKey)),
    );
    for (const r of results) if (r.status === 'fulfilled') friendEnvelopes.push(r.value);
    envelopes.push(...friendEnvelopes);
  }

  if (envelopes.length > 0) {
    try {
      await relay.publish(id, envelopes);
    } catch (err) {
      // Offline or the relay is unreachable: the edit is already safe on disk,
      // but friends don't have it yet. Remember to try again — the sync manager
      // retries this on every reconnect/focus/heartbeat until it succeeds.
      await markPublishPending();
      throw err;
    }
  }
  await clearPublishPending();

  // `published`/`skipped` speak only about sharing with friends, which is
  // what the UI reports; the self-backup above happens regardless.
  if (!hasProfile) return { published: 0, skipped: 'empty-profile' };
  if (friends.length === 0) return { published: 0, skipped: 'no-friends' };
  return { published: friendEnvelopes.length };
}

export interface AddFriendResult {
  friend: Friend;
  /** True when this re-adds someone previously removed (a tombstone). */
  revived: boolean;
  /** Why the follow-up publish did nothing, if it didn't. */
  skipped?: PublishSkip;
  /** True if the follow-up publish/pull to the relay failed (still added locally). */
  relayError: boolean;
}

/**
 * Add a Circle ID to the address book and, in the same breath, push my
 * profile to them and pull my inbox in case they'd already added me. This is
 * the one action a QR scan, an opened invite link, or a pasted Circle ID all
 * resolve to — none of them need a separate confirm step: reciprocity is
 * whatever the relay hands back, not a manual approval. Relay problems
 * degrade to `relayError`; the friend is added locally either way.
 */
export async function addFriendAndSync(circleId: string, tempName: string | undefined, me: StoredIdentity | null): Promise<AddFriendResult> {
  const { revived, ...friend } = await addFriend(circleId, tempName);
  let skipped: PublishSkip | undefined;
  let relayError = false;
  if (me) {
    try {
      // Re-adding someone previously removed: their mailbox on the relay still
      // holds my old tombstone at my current profile version, which would
      // silently reject the fresh publish as "not newer". Bump it first so it
      // lands.
      if (revived) await touchVersion();
      const res = await publishMyProfile(me);
      skipped = res.skipped;
      await syncInbox(me);
    } catch {
      relayError = true;
    }
  }
  return { friend, revived, relayError, ...(skipped !== undefined ? { skipped } : {}) };
}

/**
 * Pull my own state down from the encrypted self-backup on the relay
 * (ARCHITECTURE.md §8, §8.1) and reconcile it with what's on this device.
 * Called after a recovery, on startup, and on every sync tick — which is what
 * makes two of the user's devices converge rather than merely restore.
 *
 * The profile is adopted only if the backup's is strictly newer. Contacts and
 * groups reconcile entry by entry on their own timestamps, so an edit made on
 * the other device lands here, a removal travels as a tombstone, and a later
 * re-add beats that removal. Returns true if anything changed locally.
 */
export async function restoreMyProfileFromRelay(id: StoredIdentity): Promise<boolean> {
  if (!relay.configured) return false;

  const envelopes = await relay.fetchInbox(id);
  let best: BackupRecord | null = null;
  for (const env of envelopes) {
    // Only my own envelopes addressed to myself are self-backups.
    if (env.authorId !== id.circleId || env.recipientId !== id.circleId) continue;
    try {
      const record = await openBackupEnvelope(id.keys, id.circleId, env);
      if (!best || record.version > best.version) best = record;
    } catch {
      /* bad signature / undecryptable — ignore */
    }
  }
  if (!best) return false;

  // Anything this device backs up from now on has to out-rank what it just
  // restored — otherwise a recovery from a device whose clock ran ahead would
  // leave every future backup silently dropped by the relay.
  await raiseBackupFloor(best.version);

  let changed = false;

  const local = await getMyRecord();
  if (best.profile.version > local.version) {
    await putMyRecord(best.profile);
    changed = true;
  }

  if (best.friends.length > 0 && (await reconcileFriends(best.friends)) > 0) changed = true;
  if (best.groups.length > 0 && (await reconcileGroups(best.groups)) > 0) changed = true;

  return changed;
}

/** Verify+open one envelope, caching the sender's profile if they're a friend. */
async function processEnvelope(id: StoredIdentity, env: Envelope): Promise<boolean> {
  const friend = await getFriend(env.authorId);
  if (!friend) return false; // ignore updates from people we haven't added

  try {
    const record = await openEnvelope(id.keys, id.circleId, env);
    // A blank profile is a revocation tombstone (§9), not data. Drop the cached
    // copy instead of storing the blank: keeping it made the contact read as a
    // mutual connection with every field empty, i.e. the exact opposite of what
    // had happened.
    if (isEmptyProfile(record.profile)) {
      await removeCachedProfile(env.authorId);
      return true;
    }
    return await cacheProfileIfNewer(env.authorId, record);
  } catch {
    return false; // bad signature / not for me / version mismatch — dropped
  }
}

/** Fetch and apply all pending updates. Returns how many friends were updated. */
export async function syncInbox(id: StoredIdentity): Promise<{ updated: number }> {
  if (!relay.configured) return { updated: 0 };
  const envelopes = await relay.fetchInbox(id);
  let updated = 0;
  for (const env of envelopes) {
    if (await processEnvelope(id, env)) updated++;
  }
  return { updated };
}

/**
 * Revoke a friend (ARCHITECTURE.md §9). Removes them locally, then — if the
 * relay is on — sends a tombstone (a blank profile at a newer version) to erase
 * our data from their honest client and the relay, and rotates the content key
 * by republishing to the remaining friends (they never receive the new key).
 *
 * Honest caveat: whatever they already downloaded cannot be un-sent; the real
 * guarantee is that they receive no future updates.
 */
export async function revokeFriend(id: StoredIdentity, circleId: string): Promise<void> {
  await removeFriend(circleId);
  await removeCachedProfile(circleId);

  if (!relay.configured) return;

  // Bump version so the tombstone and the re-publish both supersede prior data.
  const bumped = await touchVersion();
  const tombstone = { version: bumped.version, updatedAt: Date.now(), profile: emptyProfile() };

  try {
    const env = await buildEnvelopeForRecipient(id.keys, id.circleId, circleId, tombstone);
    await relay.publish(id, [env]);
  } catch {
    /* relay unreachable — local removal still holds; they get no new updates */
  }

  // Rotate: fresh content key wrapped only for the remaining friends.
  try {
    await publishMyProfile(id);
  } catch {
    /* best-effort */
  }
}

/** Open a live subscription; onUpdate fires with the Circle ID that changed. */
export async function startRealtime(id: StoredIdentity, onUpdate: (circleId: string) => void): Promise<() => void> {
  if (!relay.configured) return () => {};
  return relay.subscribe(id, (env) => {
    // My own backup, pushed by another of my devices: reconcile immediately so
    // an edit made in the browser shows up in the PWA without waiting for the
    // next heartbeat. (The relay fans out to every socket on my mailbox, so
    // this also echoes back to the device that wrote it — reconciling against
    // its own state is a no-op.)
    if (env.authorId === id.circleId && env.recipientId === id.circleId) {
      void restoreMyProfileFromRelay(id).then((changed) => {
        if (changed) onUpdate(id.circleId);
      });
      return;
    }
    void processEnvelope(id, env).then((changed) => {
      if (changed) onUpdate(env.authorId);
    });
  });
}
